// src/pages/Public/SurveyGenie/PublicSurveyForm.js

import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
    Box, Paper, Typography, Button, TextField, Radio, RadioGroup,
    FormControlLabel, Checkbox, FormGroup, FormControl,
    Select, MenuItem, Slider, Rating, LinearProgress,
    CircularProgress, Card, CardContent, FormHelperText, Grid, Chip,
    Container, Stack, Alert
} from '@mui/material';
import {
    NavigateNext, NavigateBefore, CheckCircle, Error,
    CloudUpload, Star, StarBorder
} from '@mui/icons-material';
import axios from 'axios';
import backendURL from '../../../../config';
import { QUESTION_TYPE } from '../../Campaigns/SurveyGenie/surveyEnums';

const PublicSurveyForm = () => {
    const { surveyUuid } = useParams();
    
    // State Management
    const [survey, setSurvey] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [responseUuid, setResponseUuid] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [answers, setAnswers] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [completed, setCompleted] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});

    // 1. Fetch Survey Configuration
    const fetchSurvey = useCallback(async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${backendURL}/api/public/survey`, {
                params: { survey_uuid: surveyUuid }
            });
            
            if (response.data.success) {
                // FIX: Combine survey metadata and questions into one object
                const surveyData = response.data.data.survey;
                surveyData.questions = response.data.data.questions || []; 
                setSurvey(surveyData);
            } else {
                setError(response.data.message || 'Survey not found');
            }
        } catch (err) {
            console.error('Error fetching survey:', err);
            if (err.response && err.response.data && err.response.data.message) {
                setError(err.response.data.message);
            } else {
                setError('Unable to load survey. Please try again later.');
            }
        } finally {
            setLoading(false);
        }
    }, [surveyUuid]);

    useEffect(() => {
        if (surveyUuid) {
            fetchSurvey();
        }
    }, [fetchSurvey, surveyUuid]);

    // 2. Start Survey Session
    useEffect(() => {
        const startSession = async () => {
            if (survey && !responseUuid && !error && !completed) {
                try {
                    const response = await axios.post(`${backendURL}/api/public/survey/start`, { 
                        survey_uuid: surveyUuid 
                    });
                    
                    if (response.data.success) {
                        setResponseUuid(response.data.data.response_uuid);
                    }
                } catch (err) {
                    console.error('Error starting survey session:', err);
                }
            }
        };
        
        startSession();
    }, [survey, surveyUuid, responseUuid, error, completed]);

    // 3. Helper Functions
    const getQuestionsForPage = () => {
        if (!survey?.questions) return [];
        return survey.questions.filter(q => (q.page_number || 1) === currentPage);
    };

    const getTotalPages = () => {
        if (!survey?.questions) return 1;
        // Handle cases where page_number might be null
        const pageNumbers = survey.questions.map(q => q.page_number || 1);
        return pageNumbers.length > 0 ? Math.max(...pageNumbers) : 1;
    };

    const getProgress = () => {
        const totalQuestions = survey?.questions?.length || 0;
        if (totalQuestions === 0) return 0;
        const answeredQuestions = Object.keys(answers).length;
        return (answeredQuestions / totalQuestions) * 100;
    };

    // 4. Validation
    const validateCurrentPage = () => {
        const errors = {};
        const pageQuestions = getQuestionsForPage();
        
        pageQuestions.forEach(question => {
            if (question.is_required && !isQuestionAnswered(question.id)) {
                errors[question.id] = 'This question is required';
            }
        });
        
        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const isQuestionAnswered = (questionId) => {
        const answer = answers[questionId];
        if (answer === undefined || answer === null || answer === '') return false;
        if (Array.isArray(answer) && answer.length === 0) return false;
        return true;
    };

    // 5. Handling Answers
    const handleAnswerChange = (questionId, value) => {
        setAnswers(prev => ({
            ...prev,
            [questionId]: value
        }));
        
        if (validationErrors[questionId]) {
            setValidationErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[questionId];
                return newErrors;
            });
        }
        
        submitAnswer(questionId, value);
    };

    const submitAnswer = async (questionId, value) => {
        if (!responseUuid) return;
        
        try {
            const payload = {
                response_uuid: responseUuid,
                question_id: questionId
            };

            if (typeof value === 'number') {
                payload.answer_numeric = value;
            } else if (Array.isArray(value)) {
                payload.answer_json = value;
            } else if (typeof value === 'object' && value !== null) {
                payload.answer_json = value;
            } else {
                payload.answer_text = value;
            }

            await axios.post(`${backendURL}/api/public/survey/answer`, payload);
        } catch (err) {
            console.error('Error submitting answer:', err);
        }
    };

    // 6. Navigation
    const handleNext = () => {
        if (validateCurrentPage()) {
            setCurrentPage(prev => Math.min(prev + 1, getTotalPages()));
            window.scrollTo(0, 0);
        }
    };

    const handlePrevious = () => {
        setCurrentPage(prev => Math.max(prev - 1, 1));
        window.scrollTo(0, 0);
    };

    const handleSubmit = async () => {
        if (!validateCurrentPage()) return;
        
        setSubmitting(true);
        try {
            const response = await axios.post(`${backendURL}/api/public/survey/complete`, { 
                response_uuid: responseUuid 
            });
            
            if (response.data.success) {
                setCompleted(true);
                window.scrollTo(0, 0);
            } else {
                setError(response.data.message || 'Error submitting survey');
            }
        } catch (err) {
            console.error('Error completing survey:', err);
            setError('Unable to submit survey. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    // 7. Render Input Logic
    const renderQuestionInput = (question, value, hasError) => {
        switch (question.question_type) {
            case QUESTION_TYPE.SINGLE_CHOICE:
            case QUESTION_TYPE.YES_NO:
                return (
                    <RadioGroup
                        value={value || ''}
                        onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                    >
                        {question.question_type === QUESTION_TYPE.YES_NO && (!question.options || question.options.length === 0) ? (
                            <>
                                <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                                <FormControlLabel value="no" control={<Radio />} label="No" />
                            </>
                        ) : (
                            question.options?.map((option, idx) => (
                                <FormControlLabel
                                    key={idx}
                                    value={option.option_value || option.option_text || option.text || option.value} 
                                    control={<Radio />}
                                    label={option.option_text || option.text}
                                />
                            ))
                        )}
                    </RadioGroup>
                );

            case QUESTION_TYPE.MULTIPLE_CHOICE:
                return (
                    <FormGroup>
                        {question.options?.map((option, idx) => {
                            // Normalize text/value access
                            const optText = option.option_text || option.text;
                            const optValue = option.option_value || option.value || optText;
                            return (
                                <FormControlLabel
                                    key={idx}
                                    control={
                                        <Checkbox
                                            checked={(value || []).includes(optValue)}
                                            onChange={(e) => {
                                                const currentValues = value || [];
                                                const newValues = e.target.checked
                                                    ? [...currentValues, optValue]
                                                    : currentValues.filter(v => v !== optValue);
                                                handleAnswerChange(question.id, newValues);
                                            }}
                                        />
                                    }
                                    label={optText}
                                />
                            );
                        })}
                    </FormGroup>
                );

            case QUESTION_TYPE.TEXT_SHORT:
            case QUESTION_TYPE.EMAIL:
            case QUESTION_TYPE.PHONE:
                return (
                    <TextField
                        fullWidth
                        value={value || ''}
                        onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                        error={hasError}
                        placeholder="Enter your answer"
                        type={question.question_type === QUESTION_TYPE.EMAIL ? 'email' : 'text'}
                    />
                );

            case QUESTION_TYPE.TEXT_LONG:
                return (
                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        value={value || ''}
                        onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                        error={hasError}
                        placeholder="Enter your answer"
                    />
                );

            case QUESTION_TYPE.DROPDOWN:
                return (
                    <FormControl fullWidth error={hasError}>
                        <Select
                            value={value || ''}
                            onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                            displayEmpty
                        >
                            <MenuItem value="" disabled>Select an option</MenuItem>
                            {question.options?.map((option, idx) => {
                                 const optText = option.option_text || option.text;
                                 const optValue = option.option_value || option.value || optText;
                                return (
                                    <MenuItem key={idx} value={optValue}>
                                        {optText}
                                    </MenuItem>
                                );
                            })}
                        </Select>
                    </FormControl>
                );

            case QUESTION_TYPE.RATING_SCALE:
                const maxRating = question.settings?.max_value || 5;
                return (
                    <Box>
                        <Rating
                            value={parseInt(value) || 0}
                            onChange={(e, newValue) => handleAnswerChange(question.id, newValue)}
                            max={maxRating}
                            size="large"
                            icon={<Star fontSize="inherit" />}
                            emptyIcon={<StarBorder fontSize="inherit" />}
                        />
                    </Box>
                );

            case QUESTION_TYPE.LIKERT_SCALE:
                const likertOptions = question.options || [
                    { text: 'Strongly Disagree', value: '1' },
                    { text: 'Disagree', value: '2' },
                    { text: 'Neutral', value: '3' },
                    { text: 'Agree', value: '4' },
                    { text: 'Strongly Agree', value: '5' }
                ];
                return (
                    <Grid container spacing={1}>
                        {likertOptions.map((option, idx) => {
                             const optText = option.option_text || option.text;
                             const optValue = option.option_value || option.value || optText;
                            return (
                                <Grid item key={idx}>
                                    <Button
                                        variant={value === optValue ? 'contained' : 'outlined'}
                                        onClick={() => handleAnswerChange(question.id, optValue)}
                                        sx={{ minWidth: 80, textTransform: 'none' }}
                                    >
                                        {optText}
                                    </Button>
                                </Grid>
                            );
                        })}
                    </Grid>
                );

            case QUESTION_TYPE.NET_PROMOTER_SCORE:
                return (
                    <Box>
                        <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                            0 = Not Likely, 10 = Extremely Likely
                        </Typography>
                        <Grid container spacing={1}>
                            {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                                <Grid item key={num}>
                                    <Button
                                        variant={parseInt(value) === num ? 'contained' : 'outlined'}
                                        color={num <= 6 ? 'error' : num <= 8 ? 'warning' : 'success'}
                                        onClick={() => handleAnswerChange(question.id, num)}
                                        sx={{ minWidth: 40 }}
                                    >
                                        {num}
                                    </Button>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                );

            case QUESTION_TYPE.SLIDER:
                const min = question.settings?.min_value || 0;
                const max = question.settings?.max_value || 100;
                const step = question.settings?.step || 1;
                return (
                    <Box px={2} py={1}>
                        <Slider
                            value={typeof value === 'number' ? value : min}
                            onChange={(e, newValue) => handleAnswerChange(question.id, newValue)}
                            min={min}
                            max={max}
                            step={step}
                            valueLabelDisplay="auto"
                        />
                    </Box>
                );

            case QUESTION_TYPE.NUMBER:
                return (
                    <TextField
                        type="number"
                        fullWidth
                        value={value || ''}
                        onChange={(e) => handleAnswerChange(question.id, parseFloat(e.target.value))}
                        error={hasError}
                        inputProps={{
                            min: question.settings?.min_value,
                            max: question.settings?.max_value
                        }}
                    />
                );

            case QUESTION_TYPE.DATE:
                return (
                    <TextField
                        type="date"
                        fullWidth
                        value={value || ''}
                        onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                        error={hasError}
                        InputLabelProps={{ shrink: true }}
                    />
                );

            default:
                return (
                    <TextField
                        fullWidth
                        value={value || ''}
                        onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                        error={hasError}
                        placeholder="Type your answer here..."
                    />
                );
        }
    };

    // 8. Render States
    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" bgcolor="grey.100">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" bgcolor="grey.100" p={2}>
                <Paper sx={{ p: 4, maxWidth: 500, textAlign: 'center', width: '100%' }}>
                    <Error sx={{ fontSize: 60, color: 'error.main', mb: 2 }} />
                    <Typography variant="h5" gutterBottom>Survey Unavailable</Typography>
                    <Typography color="text.secondary">{error}</Typography>
                </Paper>
            </Box>
        );
    }

    if (completed) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" bgcolor="grey.100" p={2}>
                <Paper sx={{ p: 4, maxWidth: 600, textAlign: 'center', width: '100%' }}>
                    <CheckCircle sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
                    <Typography variant="h4" gutterBottom>Thank You!</Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
                        {survey?.thank_you_message || 'Your response has been recorded.'}
                    </Typography>
                </Paper>
            </Box>
        );
    }

    const totalPages = getTotalPages();
    const isLastPage = currentPage === totalPages;

    return (
        <Box minHeight="100vh" bgcolor={survey?.background_color || "grey.100"} py={4}>
            <Container maxWidth="md">
                {/* Header */}
                <Paper sx={{ p: 4, mb: 3, borderTop: `6px solid ${survey?.primary_color || '#1976d2'}` }}>
                    {survey?.logo_url && (
                        <Box mb={3} textAlign="center">
                            <img src={survey.logo_url} alt="Logo" style={{ maxHeight: 80, maxWidth: '100%' }} />
                        </Box>
                    )}
                    <Typography variant="h4" gutterBottom fontWeight="bold" color="text.primary">
                        {survey?.title}
                    </Typography>
                    {survey?.description && (
                        <Typography variant="body1" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
                            {survey.description}
                        </Typography>
                    )}
                    {survey?.estimated_time_minutes && (
                        <Chip label={`Estimated time: ${survey.estimated_time_minutes} min`} size="small" sx={{ mt: 2 }} variant="outlined" />
                    )}
                </Paper>

                {/* Progress */}
                {survey?.show_progress_bar && (
                    <Box mb={3} px={1}>
                        <Box display="flex" justifyContent="space-between" mb={1}>
                            <Typography variant="caption" color="text.secondary">Progress</Typography>
                            <Typography variant="caption" color="text.secondary">{Math.round(getProgress())}%</Typography>
                        </Box>
                        <LinearProgress 
                            variant="determinate" 
                            value={getProgress()} 
                            sx={{ height: 8, borderRadius: 4, backgroundColor: 'grey.300', '& .MuiLinearProgress-bar': { backgroundColor: survey?.primary_color || '#1976d2' }}} 
                        />
                    </Box>
                )}

                {/* Questions */}
                <Stack spacing={3}>
                    {getQuestionsForPage().map(question => {
                        const hasError = !!validationErrors[question.id];
                        const answerValue = answers[question.id];

                        return (
                            <Card key={question.id} sx={{ borderLeft: hasError ? '4px solid #d32f2f' : 'none', transition: '0.3s', '&:hover': { boxShadow: 4 } }}>
                                <CardContent sx={{ p: 3 }}>
                                    <Typography variant="h6" gutterBottom fontWeight={500}>
                                        {question.question_text}
                                        {question.is_required && <Typography component="span" color="error.main" sx={{ ml: 0.5 }}>*</Typography>}
                                    </Typography>
                                    
                                    {question.description && (
                                        <Typography variant="body2" color="text.secondary" mb={3} sx={{ fontStyle: 'italic' }}>
                                            {question.description}
                                        </Typography>
                                    )}
                                    
                                    <Box mt={2}>
                                        {renderQuestionInput(question, answerValue, hasError)}
                                    </Box>
                                    
                                    {hasError && (
                                        <FormHelperText error sx={{ mt: 1 }}>{validationErrors[question.id]}</FormHelperText>
                                    )}
                                </CardContent>
                            </Card>
                        );
                    })}
                </Stack>

                {/* Footer Navigation */}
                <Box display="flex" justifyContent="space-between" mt={4} mb={4}>
                    <Button
                        variant="outlined"
                        startIcon={<NavigateBefore />}
                        onClick={handlePrevious}
                        disabled={currentPage === 1}
                        sx={{ visibility: currentPage === 1 ? 'hidden' : 'visible' }}
                    >
                        Previous
                    </Button>
                    
                    {isLastPage ? (
                        <Button
                            variant="contained"
                            onClick={handleSubmit}
                            disabled={submitting}
                            size="large"
                            sx={{ bgcolor: survey?.primary_color || 'primary.main', '&:hover': { bgcolor: survey?.primary_color ? `${survey.primary_color}dd` : 'primary.dark' }, px: 4 }}
                        >
                            {submitting ? <CircularProgress size={24} color="inherit" /> : 'Submit'}
                        </Button>
                    ) : (
                        <Button
                            variant="contained"
                            endIcon={<NavigateNext />}
                            onClick={handleNext}
                            sx={{ bgcolor: survey?.primary_color || 'primary.main', '&:hover': { bgcolor: survey?.primary_color ? `${survey.primary_color}dd` : 'primary.dark' } }}
                        >
                            Next
                        </Button>
                    )}
                </Box>

                <Box textAlign="center" py={2}>
                    <Typography variant="caption" color="text.secondary">Powered by SurveyGenie</Typography>
                </Box>
            </Container>
        </Box>
    );
};

export default PublicSurveyForm;